import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulerService } from '../shared/services/scheduler.service';
import { XhrService } from '../shared/services/xhr.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ContextService, SharedContext } from '../shared/shared-context/context.service';
import { AutoSuggestConfigService } from './auto-suggest-config.service';

interface RawSharePointContextResourceResponse {
    Urls: Array<string>;
    Payload: string;
}

interface SharePointContextResourceResponse {
    Urls: Array<string>;
    Payload: SharePointContextResourcePayload;
}

interface SharePointContextResourcePayload {
    PortalUrl: string;
    MySiteUrl: string;
    SearchCenterUrl: string;
    OfficeGraphEnabled: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SharePointService extends XhrService {
    private sharePointContextPromise: Promise<SharePointContextResourceResponse>;
    private readonly sharedContext: SharedContext;

    constructor(
        accessTokenService: AccessTokenService,
        private readonly autoSuggestConfigService: AutoSuggestConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        contextService: ContextService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.SharePoint, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        this.sharedContext = contextService.sharedContext;

        this.getSharePointContextResource();
    }

    public getSharePointContextResource(): Promise<SharePointContextResourceResponse> {
        if (this.sharePointContextPromise) {
            return this.sharePointContextPromise;
        }

        if (!this.sharedContext.sharePointResourceId || !this.autoSuggestConfigService.config.sharePointContextInitPath || !this.capabilitiesService.capabilities.showSearch) {
            return Promise.reject('SharePoint Context not enabled');
        }

        this.sharePointContextPromise = new Promise<SharePointContextResourceResponse>((resolve, reject) => {
            const url = `${this.sharedContext.sharePointResourceId}${this.autoSuggestConfigService.config.sharePointContextInitPath}`;

            const promiseCallback = (promise: InstrumentedHttpPromise<RawSharePointContextResourceResponse>) => {
                promise
                    .then(({ data }) => {
                        const parsedData = { ...data, Payload: JSON.parse(data.Payload) };
                        resolve(parsedData);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.kp3Jp);
                    });
            };

            const finalErrorCallback = error => {
                // clear the promise so retry is possible.
                this.sharePointContextPromise = null;
                // reject to free any waiting callers
                reject(error);
            };

            this.get<RawSharePointContextResourceResponse>(
                KnownInstrumentationEvents.SharePointContextRequest,
                url,
                promiseCallback,
                finalErrorCallback,
                {},
                FetchStrategy.NetworkOnly);
        });

        return this.sharePointContextPromise;
    }
}
